/**
 * Whitelabel brand — the single source of truth for every user-facing brand
 * string (names, emails, hotline, legal entity, marketing URLs, image assets,
 * campaign/feature toggles).
 *
 * The active brand is chosen at BUILD TIME from `import.meta.env.APP_BRAND`
 * (injected by rspack from `.env.<mode>`). Add a brand by creating a profile in
 * `src/config/brands/` and registering it below; build it with `APP_BRAND=<id>`.
 * Unset/unknown falls back to the default `vnetwork` profile, so existing builds
 * are unaffected.
 *
 * Out of scope (kept in their own config on purpose):
 *   - Window/tab title + favicon → `app.config.json` + per-brand `APP_TITLE` /
 *     `APP_FAVICON` env (consumed by rspack)
 *   - Service / API host URLs, Firebase, Keycloak, Sentry → `src/config/env.ts`
 *     (`.env.<mode>`)
 *   - Design tokens (`--vn-*`) → the `@vnetwork/ui` package
 */
import type { BrandProfile } from './brands/types';
import { vnetwork } from './brands/vnetwork';
import { techvalley } from './brands/techvalley';

/** Registry of build-selectable brand profiles, keyed by `APP_BRAND`. */
const BRANDS: Record<string, BrandProfile> = {
  [vnetwork.id]: vnetwork,
  [techvalley.id]: techvalley,
};

/** Active brand id from the build (defaults to the vnetwork profile). */
const activeBrandId = (import.meta.env.APP_BRAND || vnetwork.id).trim();

/** The resolved active brand profile. */
export const brand: BrandProfile = BRANDS[activeBrandId] ?? vnetwork;

/**
 * Copyright line, e.g. "© 2013 VNETWORK JOINT STOCK COMPANY. All Rights Reserved".
 * Defaults to the founding year (`brand.since`); pass a year to override.
 */
export function brandCopyright(year: number = brand.since): string {
  return `© ${year} ${brand.legalName} All Rights Reserved`;
}

export type { BrandProfile };
export type Brand = BrandProfile;
